import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, Copy, Check } from "lucide-react";

// TOTP enrollment flow. `enrollToken` is held in memory only (never persisted).
export const EnrollTwoFactor = ({ enrollToken, onComplete }) => {
  const { enrollStart, enrollVerify } = useAuth();
  const [uri, setUri] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let live = true;
    enrollStart(enrollToken).then((d) => { if (live) { setUri(d.otpauth_uri); setSecret(d.secret); } })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
    return () => { live = false; };
    // eslint-disable-next-line
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const d = await enrollVerify(code, enrollToken);
      setCodes(d.recovery_codes || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  const qrSrc = uri ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(uri)}` : "";

  if (codes) return (
    <div data-testid="recovery-codes-step">
      <div className="flex items-center gap-2 text-[#1F4D2E] mb-2"><ShieldCheck size={22} /><h2 className="font-admin text-xl font-bold">Save your recovery codes</h2></div>
      <p className="text-sm text-[#52525B] mb-4">Each code works once. Store them somewhere safe — they are the only way in if you lose your authenticator. They will not be shown again.</p>
      <pre className="bg-[#F4F4F5] rounded-lg p-4 text-sm font-mono grid grid-cols-2 gap-1" data-testid="recovery-codes">{codes.join("\n")}</pre>
      <button type="button" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); setCopied(true); }} className="mt-3 text-sm text-[#1F4D2E] inline-flex items-center gap-1.5 hover:underline" data-testid="copy-codes">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy codes"}</button>
      <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="codes-saved-check" /> I have saved my recovery codes</label>
      <button disabled={!saved} onClick={onComplete} data-testid="enroll-continue" className="mt-5 w-full bg-[#0A0A0A] text-white rounded-md py-3 font-medium disabled:opacity-50">Continue to dashboard</button>
    </div>
  );

  return (
    <div data-testid="enroll-2fa-step">
      <div className="flex items-center gap-2 text-[#1F4D2E] mb-1"><ShieldCheck size={22} /><h2 className="font-admin text-xl font-bold">Set up two-factor authentication</h2></div>
      <p className="text-sm text-[#52525B] mb-5">Two-factor authentication is required for this account. Scan the code below with Google Authenticator, Microsoft Authenticator, Authy or 1Password.</p>
      {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="enroll-error">{error}</div>}
      {!uri ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div> : (
        <form onSubmit={verify}>
          <div className="flex justify-center mb-3"><img src={qrSrc} alt="Authenticator QR code" width={200} height={200} className="rounded-lg border border-gray-200" data-testid="totp-qr" /></div>
          <p className="text-xs text-center text-[#52525B] mb-4">Can't scan? Enter this key manually: <span className="font-mono break-all" data-testid="totp-secret">{secret}</span></p>
          <label className="block text-sm font-medium mb-1.5">Enter the 6-digit code</label>
          <input required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="enroll-code" className="w-full border border-gray-200 rounded-md px-4 py-2.5 mb-4 tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]" placeholder="000000" />
          <button disabled={busy || code.length !== 6} data-testid="enroll-verify" className="w-full bg-[#0A0A0A] text-white rounded-md py-3 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="animate-spin" size={18} /> : "Verify & activate"}</button>
        </form>
      )}
    </div>
  );
};
